import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ArrowDown, ArrowUp, FileText, Home, Plus, Type,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import {
  createHubCategory, createHubPage, loadHub, loadProducts, loadTranslationCatalogue, setHubCategoryOrder, setHubPageOrder,
  updateHubCategory,
} from './api';
import { CategoryField, WordField, friendly } from './HubFields';
import HubPageEditor from './HubPageEditor';
import { tx } from './i18n';
import {
  Alert, Chooser, Failed, Head, Loading, Pill,
} from './parts';
import { Switch } from './ProductOptions';
import { SHOP_HOST } from './ProductPageLive';
import {
  ENGLISH, SITE_TEXT, activeLanguages, ownWords, saveTexts, unix, wordsOf,
} from './siteWords';
import SiteLanguage from './SiteLanguage';
import { ForStoreOffice, StoreOfficeLine } from './StoreOffice';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import './productDetail.css';
import './help-centre.css';
import './hub.css';

// The storefront's Longevity Hub, written here.
//
// The Hub is its first page (/hub/) and its guides and articles, each in a
// category. Every page is built from sections a person adds, fills, orders
// and switches on or off (HubPageEditor.js); XOmics keeps what there is, in
// which order and with which photos (/Hub), and the words are storefront
// site text, read and written through the translation endpoints, one
// language at a time, like the help centre's and a product page's.
//
// A guide or an article is written as a draft and published with its switch;
// until then it has no page on the store. What there is, its order and its
// photos are the same in every language. Nothing is deleted: a page is
// unpublished, a section is switched off or removed from the page.

// A title as the last part of an address: its letters and digits, dashes between.
const slug = (words) => String(words).normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);

function useForm(initial) {
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const set = (name) => (value) => setValues((current) => ({ ...current, [name]: value }));
  const send = async (work) => {
    setBusy(true);
    setFailed('');
    try { await work(values); } catch (error) { setFailed(errorMessage(error)); } finally { setBusy(false); }
  };
  return { values, set, busy, failed, send };
}

function NewPage({ hub, onCreated, onCancel }) {
  const form = useForm({ title: '', categoryId: hub.categories.find((category) => category.isActive)?.id ?? null, address: '' });
  const { title, categoryId, address } = form.values;
  const category = hub.categories.find((entry) => entry.id === categoryId);
  // The address follows the title until a person writes their own.
  const folder = category ? slug(category.name.value.split(' ')[0]) : '';
  const suggested = [folder, slug(title)].filter(Boolean).join('/');
  const shown = address !== '' ? address : suggested;
  return <form className="xo-hub-form" onSubmit={(event) => {
    event.preventDefault();
    form.send(async () => onCreated(await createHubPage({ title: title.trim(), categoryId, path: `/hub/${shown.replace(/^\/+|\/+$/g, '')}/` })));
  }}>
    <h2 id="xo-hub-new-page--form">{tx('ui.hub.new_page.title')}</h2>
    <div className="xo-hub-field">
      <label htmlFor="xo-hub-new-title">{tx('ui.hub.new_page.name')}</label>
      <input id="xo-hub-new-title" className="xo-field" type="text" value={title} maxLength={300} autoFocus
        onChange={(event) => form.set('title')(event.target.value)} />
    </div>
    <CategoryField value={categoryId} categories={hub.categories} onChange={form.set('categoryId')} />
    <div className="xo-hub-field">
      <label htmlFor="xo-hub-new-address">{tx('ui.hub.settings.address')}</label>
      <div className="xo-hub-address-box"><span>{SHOP_HOST}/hub/</span>
        <input id="xo-hub-new-address" className="xo-field" type="text" value={shown} spellCheck={false}
          onChange={(event) => form.set('address')(event.target.value.toLowerCase().replace(/\s+/g, '-'))} /><span>/</span></div>
    </div>
    {form.failed && <Alert tone="bad">{form.failed}</Alert>}
    <div className="xo-hub-form-foot">
      <button type="button" className="xo-btn-2" onClick={onCancel}>{tx('ui.hub.cancel')}</button>
      <button type="submit" className="xo-btn" disabled={form.busy || title.trim() === '' || shown === ''}>
        {tx('ui.hub.new_page.create')}</button>
    </div>
  </form>;
}

function NewCategory({ onCreated, onCancel }) {
  const form = useForm({ name: '', colour: '#2457c5' });
  const { name, colour } = form.values;
  return <form className="xo-hub-form" onSubmit={(event) => {
    event.preventDefault();
    form.send(async () => onCreated(await createHubCategory({ name: name.trim(), colour })));
  }}>
    <h2 id="xo-hub-new-category--form">{tx('ui.hub.new_category.title')}</h2>
    <div className="xo-hub-field">
      <label htmlFor="xo-hub-category-name">{tx('ui.hub.new_category.name')}</label>
      <input id="xo-hub-category-name" className="xo-field" type="text" value={name} maxLength={300} autoFocus
        onChange={(event) => form.set('name')(event.target.value)} />
    </div>
    <div className="xo-hub-field">
      <label htmlFor="xo-hub-category-colour">{tx('ui.hub.category.colour')}</label>
      <input id="xo-hub-category-colour" className="xo-hub-colour" type="color" value={colour}
        onChange={(event) => form.set('colour')(event.target.value)} />
    </div>
    {form.failed && <Alert tone="bad">{form.failed}</Alert>}
    <div className="xo-hub-form-foot">
      <button type="button" className="xo-btn-2" onClick={onCancel}>{tx('ui.hub.cancel')}</button>
      <button type="submit" className="xo-btn" disabled={form.busy || name.trim() === ''}>
        {tx('ui.hub.new_category.create')}</button>
    </div>
  </form>;
}

// A list of texts written in one go: a category's name, or the words every
// article shares.
function TextsForm({ rows, language, canManage, writing, onSaved, children }) {
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  useEffect(() => { setDrafts({}); setNotice(null); }, [language, rows.length]);
  const stored = (text) => friendly(ownWords(text, language));
  const dirty = rows.filter(({ text }) => drafts[text.key] !== undefined && unix(drafts[text.key]) !== stored(text));
  return <form className="xo-hub-texts" onSubmit={async (event) => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await saveTexts(dirty.map(({ text }) => [text, unix(drafts[text.key])]), writing);
      await onSaved();
      setDrafts({});
      setNotice({ tone: 'ok', text: tx('ui.hub.save.saved') });
    } catch (error) {
      setNotice({ tone: 'bad', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }}>
    {rows.map(({ text, label, kind = 'line' }) => <WordField key={text.key} kind={kind} label={label} text={text} language={language}
      value={drafts[text.key] !== undefined ? drafts[text.key] : stored(text)} disabled={!canManage || busy}
      hub={{ front: null, pages: [] }} products={[]}
      onChange={(value) => setDrafts((current) => ({ ...current, [text.key]: value }))} />)}
    {children}
    {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
    <div className="xo-hub-form-foot">
      <button type="submit" className="xo-btn" disabled={!canManage || busy || dirty.length === 0}>
        {busy ? tx('ui.hub.save.saving') : tx('ui.hub.save.save')}</button>
    </div>
  </form>;
}

// A category: its name, its colour, whether the store shows it, and the
// order of its pages.
function CategoryView({ category, hub, language, canManage, writing, onChanged, onOpenPage }) {
  const [failed, setFailed] = useState('');
  const pages = hub.pages.filter((page) => page.categoryId === category.id);
  const change = async (work) => {
    setFailed('');
    try { await work(); await onChanged(); } catch (error) { setFailed(errorMessage(error)); }
  };
  const move = (index, by) => {
    const mine = pages.map((page) => page.id);
    [mine[index], mine[index + by]] = [mine[index + by], mine[index]];
    // The pages of every category are one list; this category's take their new order within it.
    const places = hub.pages.map((page, at) => (page.categoryId === category.id ? at : -1)).filter((at) => at >= 0);
    const all = hub.pages.map((page) => page.id);
    places.forEach((place, at) => { all[place] = mine[at]; });
    change(() => setHubPageOrder(all));
  };
  return <div className="xo-hub-category">
    <header className="xo-hub-page-head">
      <div><h2 dir="auto"><i className="xo-hub-dot" style={{ background: category.colour }} aria-hidden="true" />
        {friendly(wordsOf(category.name, language))}</h2></div>
      <div className="xo-hub-page-tools"><div className="xo-hub-publish">
        <Pill tone={category.isActive ? 'ok' : 'flat'}>{category.isActive ? tx('ui.hub.category.shown') : tx('ui.hub.category.hidden')}</Pill>
        <Switch on={category.isActive} disabled={!canManage} label={tx('ui.hub.category.shown')}
          onChange={(isActive) => change(() => updateHubCategory(category.id, { isActive }))} />
      </div></div>
    </header>
    {failed && <Alert tone="bad">{failed}</Alert>}
    <section className="xo-hub-settings">
      <TextsForm rows={[{ text: category.name, label: tx('ui.hub.new_category.name') }]} language={language} canManage={canManage}
        writing={writing} onSaved={onChanged}>
        <div className="xo-hub-field">
          <label htmlFor="xo-hub-colour">{tx('ui.hub.category.colour')}</label>
          <input id="xo-hub-colour" className="xo-hub-colour" type="color" value={category.colour} disabled={!canManage}
            onChange={(event) => change(() => updateHubCategory(category.id, { colour: event.target.value }))} />
        </div>
      </TextsForm>
    </section>
    <section className="xo-hub-settings">
      <strong>{tx('ui.hub.category.pages')}</strong>
      {pages.length === 0 ? <p className="xo-hub-field-note">{tx('ui.hub.category.empty')}</p> : <ul className="xo-hub-rows">
        {pages.map((page, index) => <li key={page.id}>
          <span className="xo-hub-arrows">
            <button type="button" className="xo-icon-btn" disabled={!canManage || index === 0} aria-label={tx('ui.hub.section.move_up')}
              onClick={() => move(index, -1)}><ArrowUp aria-hidden="true" /></button>
            <button type="button" className="xo-icon-btn" disabled={!canManage || index === pages.length - 1}
              aria-label={tx('ui.hub.section.move_down')} onClick={() => move(index, 1)}><ArrowDown aria-hidden="true" /></button>
          </span>
          <button type="button" className="xo-link" onClick={() => onOpenPage(page.id)} dir="auto">
            {friendly(wordsOf(page.title, language)) || page.path}</button>
          <Pill tone={page.isPublished ? 'ok' : 'flat'}>{page.isPublished ? tx('ui.hub.page.published') : tx('ui.hub.side.draft')}</Pill>
        </li>)}
      </ul>}
    </section>
  </div>;
}

// The Hub is one, shared by the offices; which of its pages, categories and
// parts an office's store shows is that office's own, so the page is one office's.
export default function XomicsHub() {
  return <ForStoreOffice title={tx('ui.hub.title')} className="xo-platform-page xo-help-page xo-hub">
    {(store) => <HubPage store={store} />}
  </ForStoreOffice>;
}

function HubPage({ store }) {
  const { permissions } = useXomicsAuth();
  const canManage = permissions.includes('CONTENT_MANAGE');
  const [language, setLanguage] = useState(ENGLISH);
  // What is open: the first page, a guide or an article, a category, the
  // shared words, or a form for something new.
  const [place, setPlace] = useState({ view: 'front' });
  // The list of what there is to open, under the name of what is open.
  const [choosing, setChoosing] = useState(false);
  const editor = useRef(null);
  const catalogue = useLiveResource(['translations'], useCallback(() => loadTranslationCatalogue(), []));
  const list = useLiveResource(['translations'], useCallback(() => loadHub(language), [language]));
  const sold = useLiveResource(['products'], useCallback(() => loadProducts(), []));
  const hub = list.data;
  const languages = useMemo(() => (catalogue.data ? activeLanguages(catalogue.data) : []), [catalogue.data]);
  const siteText = catalogue.data?.contentTypes.find((entry) => entry.code === SITE_TEXT) ?? null;
  const products = useMemo(() => (sold.data || []).filter((product) => product.isActive !== false), [sold.data]);
  const refreshList = list.refresh;
  const onListChanged = useCallback(() => { refreshList(); }, [refreshList]);

  if ((list.error && !hub) || (catalogue.error && !catalogue.data)) {
    return <Failed message={list.error || catalogue.error} onRetry={() => { list.refresh(); catalogue.refresh(); }} />;
  }
  if (!hub || !catalogue.data || !siteText) return <Loading label={tx('ui.hub.loading')} />;

  const writing = { language, contentTypeId: siteText.id, source: 'hub' };
  // Leaving a page with words that are not saved asks first.
  const go = (next) => {
    setChoosing(false);
    if (editor.current?.isDirty() && !window.confirm(tx('ui.hub.save.leave'))) return;
    setPlace(next);
  };
  const pageId = place.view === 'front' ? hub.front?.id ?? null : place.view === 'page' ? place.id : null;
  const category = place.view === 'category' ? hub.categories.find((entry) => entry.id === place.id) : null;
  // What is open, by its name, on the button that opens the list.
  const openPage = place.view === 'page' ? hub.pages.find((page) => page.id === place.id) : null;
  const here = place.view === 'front' ? { name: tx('ui.hub.side.front'), Icon: Home }
    : openPage ? { name: friendly(wordsOf(openPage.title, language)) || openPage.path, Icon: FileText }
      : category ? { name: friendly(wordsOf(category.name, language)), Icon: null }
        : place.view === 'shared' ? { name: tx('ui.hub.side.shared'), Icon: Type }
          : { name: place.view === 'new-category' ? tx('ui.hub.side.new_category') : tx('ui.hub.side.new_page'), Icon: Plus };
  const grouped = [...hub.categories.map((entry) => ({ category: entry, pages: hub.pages.filter((page) => page.categoryId === entry.id) })),
    { category: null, pages: hub.pages.filter((page) => page.categoryId === null) }].filter((group) => group.category || group.pages.length > 0);
  const moveCategory = async (index, by) => {
    const ids = hub.categories.map((entry) => entry.id);
    [ids[index], ids[index + by]] = [ids[index + by], ids[index]];
    await setHubCategoryOrder(ids);
    list.refresh();
  };
  return <div className="xo-platform-page xo-help-page xo-hub">
    <Head title={tx('ui.hub.title')} under={<StoreOfficeLine store={store} />} />
    {!canManage && <Alert tone="info">{tx('ui.hub.read_only')}</Alert>}
    {/* What is open is chosen from a list under its name, so the page it is written in has the whole
        width; a new page or category is started at the foot of that list. */}
    <div className="xo-product-navigation">
    <div className="xo-product-section-row">
      <Chooser label={tx('ui.hub.title')} name={here.name} Icon={here.Icon} open={choosing} onOpen={setChoosing}
        footer={canManage && <>
          <button type="button" onClick={() => go({ view: 'new-page' })}>
            <Plus aria-hidden="true" />{tx('ui.hub.side.new_page')}</button>
          <button type="button" onClick={() => go({ view: 'new-category' })}>
            <Plus aria-hidden="true" />{tx('ui.hub.side.new_category')}</button>
        </>}>
      <nav className="xo-help-side xo-hub-side" aria-label={tx('ui.hub.title')}>
        <ul>
          <li><button type="button" className={`xo-hub-side-front${place.view === 'front' ? ' is-on' : ''}`} onClick={() => go({ view: 'front' })}>
            <span><Home aria-hidden="true" />{tx('ui.hub.side.front')}</span></button></li>
        </ul>
        <p className="xo-hub-side-label">{tx('ui.hub.side.pages')}</p>
        <ul>
          {grouped.map((group, index) => <li key={group.category?.id ?? 'none'}>
            <div className="xo-hub-side-category">
              <button type="button" className={place.view === 'category' && place.id === group.category?.id ? 'is-on' : ''}
                disabled={!group.category} onClick={() => group.category && go({ view: 'category', id: group.category.id })}>
                <span dir="auto">{group.category && <i className="xo-hub-dot" style={{ background: group.category.colour }} aria-hidden="true" />}
                  {group.category ? friendly(wordsOf(group.category.name, language)) : tx('ui.hub.side.no_category')}</span>
                <small>{group.pages.length}</small>
              </button>
              {group.category && canManage && <span className="xo-hub-arrows">
                <button type="button" className="xo-icon-btn" disabled={index === 0} aria-label={tx('ui.hub.section.move_up')}
                  onClick={() => moveCategory(index, -1)}><ArrowUp aria-hidden="true" /></button>
                <button type="button" className="xo-icon-btn" disabled={index >= hub.categories.length - 1}
                  aria-label={tx('ui.hub.section.move_down')} onClick={() => moveCategory(index, 1)}><ArrowDown aria-hidden="true" /></button>
              </span>}
            </div>
            {group.pages.length > 0 && <ul>
              {group.pages.map((page) => <li key={page.id}>
                <button type="button" className={`${place.view === 'page' && place.id === page.id ? 'is-on' : ''}${page.isPublished ? '' : ' is-draft'}`}
                  onClick={() => go({ view: 'page', id: page.id })}>
                  <span dir="auto"><FileText aria-hidden="true" />{friendly(wordsOf(page.title, language)) || page.path}</span>
                  {!page.isPublished && <small>{tx('ui.hub.side.draft')}</small>}
                </button></li>)}
            </ul>}
          </li>)}
        </ul>
        <ul className="xo-hub-side-last">
          <li><button type="button" className={place.view === 'shared' ? 'is-on' : ''} onClick={() => go({ view: 'shared' })}>
            <span><Type aria-hidden="true" />{tx('ui.hub.side.shared')}</span></button></li>
        </ul>
      </nav>
      </Chooser>
      <div className="xo-product-toolbar-actions">
        <SiteLanguage label={tx('ui.hub.page.language')} value={language} languages={languages}
          onChange={(code) => { if (!editor.current?.isDirty() || window.confirm(tx('ui.hub.save.leave'))) setLanguage(code); }} />
      </div>
    </div>
    </div>

      <div className="xo-help-main">
        {pageId !== null && <HubPageEditor ref={editor} key={pageId} pageId={pageId} hub={hub} language={language} languages={languages}
          canManage={canManage} siteTextId={siteText.id} products={products} onListChanged={onListChanged} />}
        {place.view === 'front' && pageId === null && <Alert tone="info">{tx('ui.hub.no_front')}</Alert>}
        {category && <CategoryView category={category} hub={hub} language={language} canManage={canManage} writing={writing}
          onChanged={list.refresh} onOpenPage={(id) => go({ view: 'page', id })} />}
        {place.view === 'shared' && <section className="xo-hub-settings">
          <header className="xo-hub-page-head">
            <div><h2>{tx('ui.hub.side.shared')}</h2></div>
          </header>
          <TextsForm language={language} canManage={canManage} writing={writing} onSaved={list.refresh}
            rows={Object.entries(hub.shared).map(([name, text]) => ({ text, label: tx(`ui.hub.shared.${name}`), kind: name.endsWith('lede') ? 'text' : 'line' }))} />
        </section>}
        {place.view === 'new-page' && <NewPage hub={hub} onCancel={() => setPlace({ view: 'front' })}
          onCreated={async (created) => { await list.refresh(); setPlace({ view: 'page', id: created.id }); }} />}
        {place.view === 'new-category' && <NewCategory onCancel={() => setPlace({ view: 'front' })}
          onCreated={async (created) => { await list.refresh(); setPlace({ view: 'category', id: created.id }); }} />}
      </div>
  </div>;
}
